import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { imageSrc } from "../lib/api";
import { makeShareCard, savedName } from "../lib/shareCard";
import { ShareSheet } from "./share/ShareSheet";
import { openLetter } from "../lib/hearts";

const KEY = "afae-heart-dismissed";
const dismissed = () => new Set(JSON.parse(sessionStorage.getItem(KEY) || "[]"));
const PROTECTED = "img, canvas, [data-protect]";

async function downloadSigned(painting) {
  const { blob } = await makeShareCard(painting, null, savedName());
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${painting.slug}-signed-priyanka-jha.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  window.__afaeHeartDownload = { slug: painting.slug, type: blob.type, size: blob.size };
}

// Save attempts on a painting open a soft "Touched your heart?" card instead of the browser menu.
export function HeartGuard({ getPainting, onDetails, paintings = [] }) {
  const [card, setCard] = useState(null);
  const [share, setShare] = useState(null);
  const press = useRef(null);
  const offer = useCallback((el) => {
    const pid = el?.closest?.("[data-painting-id]")?.dataset.paintingId;
    const p = (pid && paintings.find((x) => x.id === pid)) || getPainting();
    if (!p) return false;
    if (!dismissed().has(p.id)) setCard(p);
    return true;
  }, [getPainting, paintings]);

  useEffect(() => {
    const onMenu = (e) => {
      if (!e.target.closest?.(PROTECTED)) return;
      if (offer(e.target)) e.preventDefault();
    };
    const onDrag = (e) => {
      if (e.target.closest?.(PROTECTED) && (e.target.closest("[data-painting-id]") || getPainting())) e.preventDefault();
    };
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s" && onDetails()) {
        e.preventDefault();
        offer();
      }
    };
    const clear = () => {
      clearTimeout(press.current?.t);
      press.current = null;
    };
    const onDown = (e) => {
      if (e.pointerType !== "touch" || !e.target.closest?.(PROTECTED)) return;
      const el = e.target;
      press.current = { x: e.clientX, y: e.clientY, t: setTimeout(() => offer(el), 650) };
    };
    const onMove = (e) => {
      if (press.current && Math.hypot(e.clientX - press.current.x, e.clientY - press.current.y) > 12) clear();
    };
    document.addEventListener("contextmenu", onMenu);
    document.addEventListener("dragstart", onDrag);
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", clear);
    document.addEventListener("pointercancel", clear);
    return () => {
      document.removeEventListener("contextmenu", onMenu);
      document.removeEventListener("dragstart", onDrag);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", clear);
      document.removeEventListener("pointercancel", clear);
    };
  }, [offer, getPainting, onDetails, paintings]);

  const close = () => {
    const d = dismissed();
    d.add(card.id);
    sessionStorage.setItem(KEY, JSON.stringify([...d]));
    setCard(null);
  };
  return (
    <>
      <AnimatePresence>{card && <HeartCard key={card.id} painting={card} onClose={close} onShare={() => setShare(card)} />}</AnimatePresence>
      <AnimatePresence>{share && <ShareSheet painting={share} onClose={() => setShare(null)} />}</AnimatePresence>
    </>
  );
}

function HeartCard({ painting, onClose, onShare }) {
  const [busy, setBusy] = useState(false);
  const keep = async () => {
    setBusy(true);
    try {
      await downloadSigned(painting);
    } finally {
      setBusy(false);
    }
  };
  const choice = "w-full rounded-full border border-[#7E57C2]/30 bg-white/70 px-5 py-2.5 font-hand text-xl text-[#4A2D55] transition-colors duration-200 hover:bg-white hover:text-[#7E57C2]";
  return (
    <motion.div data-testid="heart-card-overlay" className="fixed inset-0 z-[90] flex items-center justify-center bg-[#2D1E3E]/25 p-5 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        data-testid="heart-card"
        role="dialog"
        aria-label="Touched your heart?"
        initial={{ y: 16, scale: 0.97 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 10, opacity: 0 }}
        transition={{ duration: 0.35 }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
        className="flex w-full max-w-sm flex-col items-center gap-4 rounded-[2rem] bg-[#FBF7F2] px-7 py-7 text-center shadow-[0_30px_80px_-30px_rgba(45,30,62,0.6)]"
      >
        <p className="font-script text-4xl text-[#4A2D55]">Touched your heart?</p>
        <img data-testid="heart-card-thumb" src={imageSrc(painting.thumb_url || painting.image_url)} alt="" draggable={false} className="h-28 w-28 rounded-xl object-cover shadow-md" />
        <p data-testid="heart-card-text" className="font-display text-lg italic text-[#4A3660]">
          This painting is “{painting.title}” by Priyanka Jha
        </p>
        <div className="flex w-full flex-col gap-2">
          <button data-testid="heart-keep-card" onClick={keep} disabled={busy} className={choice}>
            {busy ? "signing your card…" : "Keep a signed card"}
          </button>
          <button data-testid="heart-share" onClick={onShare} className={choice}>
            Share it with love
          </button>
          <button data-testid="heart-ask" onClick={() => { onClose(); openLetter({ topic: "use", painting_slug: painting.slug, message: `Dear Priyanka, I would love to ask about using “${painting.title}”.` }); }} className={choice}>
            Ask to use this painting
          </button>
        </div>
        <div className="flex w-full items-center justify-between pt-1">
          <button data-testid="heart-save-anyway" onClick={keep} className="text-sm text-[#5C4A72] underline-offset-4 hover:underline">
            save anyway (signed copy)
          </button>
          <button data-testid="heart-card-close" onClick={onClose} className="font-hand text-lg text-[#7C6992] transition-colors duration-200 hover:text-[#4A2D55]">
            close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
